import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, theme as antTheme } from 'antd';
import ko_KR from 'antd/es/locale/ko_KR';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import Login from './pages/Login';
import Main from './components/Main';
import AttendanceReport from './components/AttendanceReport';
import AttendanceInfo from './components/AttendanceInfo';
import ProductReport from './components/ProductReport';
import ProductInfo from './components/ProductInfo';
import ProductPeople from './components/ProductPeople';
import Settings from './components/Settings';
import AppLayout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeContext, ThemeProvider } from './contexts/ThemeContext';
import MealPage from './pages/MealPage';
import OrderVolumePage from './pages/OrderVolumePage';
import CapaPage from './pages/CapaPage';
import HanssemMallPage from './pages/HanssemMallPage';
import SignupPage from './pages/SignupPage';
import StockHanssemPage from './pages/StockHanssemPage';
import UpdateDetail from './components/UpdateDetail'; // UpdateDetail 컴포넌트를 가져옵니다.
import NotFoundPage from './pages/NotFoundPage'; // NotFoundPage 컴포넌트 가져오기
import RomanCalcPage from './pages/RomanCalcPage.js'; 
import RomanSetPage from './pages/RomanSetPage.js';
import BmsDataPage from './pages/BmsDataPage';
import FinishWindowInfoPage from './pages/FinishWindowInfoPage';
import FinishWindowOrderPage from './pages/FinishWindowOrderPage';
import VendorManagementPage from './pages/VendorManagementPage'; 
import PermissionsPage from './pages/PermissionsPage';
import UpdatesAdminPage from './pages/UpdatesAdminPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import AttendancePublicPage from './pages/AttendancePublicPage';
import KakaoBotPage from './pages/KakaoBotPage';
import WorkHoursPage from './pages/WorkHoursPage';
import MyWorkHoursPage from './pages/MyWorkHoursPage';
import OvertimeInputPage from './pages/OvertimeInputPage';
import DeliverySummaryPage from './pages/DeliverySummaryPage';
// 모바일 공통 반응형 규칙 — 페이지 CSS보다 뒤에 로드되도록 마지막에 import
import './styles/Responsive.css';

dayjs.locale('ko');

const protectedRoutes = [
  { path: "/main", component: Main },
  { path: "/attendance/report", component: AttendanceReport },
  { path: "/attendance/info", component: AttendanceInfo },
  { path: "/attendance/work-hours", component: WorkHoursPage },
  { path: "/attendance/overtime-input", component: OvertimeInputPage },
  { path: "/production-tools/delivery-summary", component: DeliverySummaryPage },
  { path: "/product/report", component: ProductReport },
  { path: "/product/info", component: ProductInfo },
  { path: "/product/people", component: ProductPeople },
  { path: "/meal", component: MealPage },
  { path: "/domestic/capa", component: CapaPage },
  { path: "/hanssem-mall", component: HanssemMallPage },
  { path: "/stock/hanssem", component: StockHanssemPage },
  { path: "/domestic/order_volume", component: OrderVolumePage },
  { path: "/settings", component: Settings },
  { path: "/product/rm-calc", component: RomanCalcPage },
  { path: "/product/rm-set", component: RomanSetPage },
  { path: "/product/bms-data", component: BmsDataPage },
  { path: "/overseas/finish-window-info", component: FinishWindowInfoPage },
  { path: "/overseas/finish-window-order", component: FinishWindowOrderPage },
  { path: "/overseas/vendor-management", component: VendorManagementPage },
  { path: "/admin/permissions", component: PermissionsPage },
  { path: "/admin/updates", component: UpdatesAdminPage },
  { path: "/admin/kakao-bot", component: KakaoBotPage },
];

const AppRoutes = () => {
  const { isDark } = useContext(ThemeContext);
  return (
    // 앱 전체 한국어: antd 기본 문구(데이터 없음, 페이지네이션, 확인/취소, 달력 등)를 한글로
    <ConfigProvider locale={ko_KR} theme={{ algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm }}>
      <AntdApp>
        <AuthProvider>
          <Router>
            <Routes>
              {/* 로그인 페이지 - Public Route */}
              <Route path="/" element={<Login />} />

              {/* 회원가입 페이지 - Public Route */}
              <Route path="/signup" element={<SignupPage />} />

              {/* OIDC 소셜 로그인 콜백 - Public Route */}
              <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

              {/* 아웃소싱 관리자용 출퇴근 조회 - Public Route (로그인/AppLayout 미적용) */}
              {/* 정적 라우트(/attendance/info·report·public)가 React Router v6 랭킹상 :slug보다 우선 매칭됨 */}
              {/* 개인 근무시간 QR 링크 - Public Route (로그인 없음, 무작위 토큰) */}
              <Route path="/h/:token" element={<MyWorkHoursPage />} />
              <Route path="/my-hours/:token" element={<MyWorkHoursPage />} />
              <Route path="/attendance/public" element={<AttendancePublicPage />} />
              {/* [v2] 슬러그 공개 접근 - token 없이 기억 가능한 URL로 동일 공개 페이지 진입 */}
              <Route path="/attendance/:slug" element={<AttendancePublicPage />} />

              {/* 보호된 경로 - PrivateRoute 적용 */}
              {protectedRoutes.map(({ path, component: Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <PrivateRoute>
                      <AppLayout>
                        <Component />
                      </AppLayout>
                    </PrivateRoute>
                  }
                />
              ))}

              {/* 업데이트 상세 페이지 - PrivateRoute 적용 */}
              <Route
                path="/update/:id"
                element={
                  <PrivateRoute>
                    <AppLayout>
                      <UpdateDetail />
                    </AppLayout>
                  </PrivateRoute>
                }
              />

              {/* 404 Not Found Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Router>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

const App = () => (
  <ThemeProvider>
    <AppRoutes />
  </ThemeProvider>
);

export default App;
